import React,{useEffect,useRef,useState} from 'react';
import {useLocation} from 'react-router-dom';
import {AUTH_CHANGED_EVENT} from '../../context/CartContext';
import {isLoggedIn} from '../../utils/auth';
import {claimMarketingNotice,saveMarketingPreference} from '../../api/marketingApi';
import MarketingDialog from './MarketingDialog';
import Button from '../ui/Button';
import {watchSessionExpiry} from '../../analytics/sessionIdentity';

const session=()=>isLoggedIn()?localStorage.getItem('token'):null;
export default function MarketingNoticePrompt() {
  const {pathname}=useLocation(),[token,setToken]=useState(session),[retry,setRetry]=useState(0);
  const [prompt,setPrompt]=useState(null),[choices,setChoices]=useState({}),[busy,setBusy]=useState(false),[error,setError]=useState('');
  const request=useRef(null),handled=useRef(new Set()),failures=useRef(0);
  const interaction=useRef(0);
  const allowed=pathname!=='/'&&!/^\/(login|register|supplier-register|forgot|reset-password|marketing\/unsubscribe|checkout|pagamenti)(\/|$)/.test(pathname);
  useEffect(()=>{
    let observed=session();
    const sync=event=>{
      const next=session();
      if(next!==observed||event?.detail?.reason==='login'){
        interaction.current++;request.current=null;setPrompt(null);setBusy(false);setError('');
      }
      observed=next;setToken(next);setRetry(n=>n+1);
    };
    window.addEventListener(AUTH_CHANGED_EVENT,sync);window.addEventListener('storage',sync);
    window.addEventListener('online',sync);window.addEventListener('focus',sync);
    return()=>{window.removeEventListener(AUTH_CHANGED_EVENT,sync);window.removeEventListener('storage',sync);
      window.removeEventListener('online',sync);window.removeEventListener('focus',sync);};
  },[]);
  useEffect(()=>watchSessionExpiry(token,()=>{
    interaction.current++;request.current=null;setPrompt(null);setBusy(false);setError('');setToken(null);
  }),[token]);
  useEffect(()=>{setToken(session());},[pathname]);
  useEffect(()=>{
    let alive=true,timer;
    if(!token||session()!==token||!allowed){setPrompt(null);return;}
    if(handled.current.has(token))return;
    const generation=interaction.current;
    // Reuse the in-flight claim through StrictMode and route changes: no second POST.
    if(request.current?.token!==token)request.current={token,promise:claimMarketingNotice(token)};
    request.current.promise.then(data=>{
      if(!alive||session()!==token||generation!==interaction.current)return;
      handled.current.add(token);failures.current=0;
      if(data.show&&data.notice?.approved){
        setPrompt({token,data});
        // Existing choices remain authoritative; defaults apply only before a channel's first choice.
        setChoices(Object.fromEntries(['email','push'].filter(channel=>data.preferences[channel].revision===0)
          .map(channel=>[channel,true])));
        setError('');setBusy(false);
      }
    }).catch(()=>{
      if(!alive||session()!==token||generation!==interaction.current)return;
      request.current=null;
      // Passive retries never block navigation; online/focus also permit a retry.
      if(failures.current++<3)timer=setTimeout(()=>setRetry(n=>n+1),Math.min(30000,3000*failures.current));
    });
    return()=>{alive=false;clearTimeout(timer);};
  },[token,pathname,allowed,retry]);
  const visible=allowed&&prompt?.token===token&&session()===token;
  const close=()=>{interaction.current++;setPrompt(null);setError('');};
  async function save() {
    const active=token,generation=interaction.current,data=prompt.data;
    setBusy(true);setError('');
    try {
      const updates=Object.keys(choices).map(channel=>({channel,enabled:choices[channel],revision:data.preferences[channel].revision}));
      if(updates.length)await saveMarketingPreference(active,{choices:updates,noticeVersion:data.notice.version,origin:'notice'});
      if(session()===active&&interaction.current===generation)close();
    } catch {if(session()===active&&interaction.current===generation)setError('Salvataggio non completato. Puoi riprovare o gestire le preferenze dal profilo.');}
    finally {if(session()===active&&interaction.current===generation)setBusy(false);}
  }
  return <MarketingDialog open={!!visible} title="Non perderti le novità di StirUp" closeLabel="Non ora" onClose={close}
    primary={<Button disabled={busy} onClick={save}>Salva preferenze</Button>}>
    <p>Ora puoi scegliere come ricevere offerte, promozioni e novità. Le comunicazioni sono facoltative e puoi modificare le preferenze in qualsiasi momento dal tuo profilo.</p>
    {['email','push'].map(channel=><label key={channel} style={{display:'block'}}><input type="checkbox" disabled={busy}
      checked={choices[channel]??prompt?.data.preferences[channel].enabled??false} onChange={e=>setChoices({...choices,[channel]:e.target.checked})}/>
      {channel==='email'?'Offerte e novità via email.':'Offerte e novità tramite notifiche push.'}</label>)}
    {prompt&&<p><a href={prompt.data.notice.url} target="_blank" rel="noopener noreferrer">Informativa marketing — {prompt.data.notice.version}</a></p>}
    {error&&<p role="alert">{error}</p>}
  </MarketingDialog>;
}
